import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api, { inr } from "@/lib/api";
import Logo from "@/components/Logo";

// Number to words (Indian) — simple version for invoices
function numToWords(num) {
  const a = ["","One","Two","Three","Four","Five","Six","Seven","Eight","Nine","Ten",
    "Eleven","Twelve","Thirteen","Fourteen","Fifteen","Sixteen","Seventeen","Eighteen","Nineteen"];
  const b = ["","","Twenty","Thirty","Forty","Fifty","Sixty","Seventy","Eighty","Ninety"];
  const inWords = n => n < 20 ? a[n] : b[Math.floor(n/10)] + (n%10 ? " " + a[n%10] : "");
  const build = n => {
    if (n === 0) return "";
    let s = "";
    if (n >= 10000000) { s += build(Math.floor(n/10000000)) + " Crore "; n %= 10000000; }
    if (n >= 100000) { s += build(Math.floor(n/100000)) + " Lakh "; n %= 100000; }
    if (n >= 1000) { s += build(Math.floor(n/1000)) + " Thousand "; n %= 1000; }
    if (n >= 100) { s += a[Math.floor(n/100)] + " Hundred "; n %= 100; }
    if (n > 0) s += inWords(n);
    return s.trim();
  };
  const int = Math.floor(num); const paise = Math.round((num - int) * 100);
  let out = build(int) || "Zero";
  out = "Rupees " + out + (paise > 0 ? ` and ${build(paise)} Paise` : "") + " Only";
  return out;
}

// ---- Shared print shell ----
function PrintShell({ children }) {
  useEffect(() => {
    const t = setTimeout(() => { try { window.print(); } catch { /* ignore print errors */ } }, 900);
    return () => clearTimeout(t);
  }, []);
  return (
    <div className="print-page" style={{ background:"#fff", color:"#111" }}>
      <style>{`
        @page { size: A4 portrait; margin: 8mm; }
        html, body { background: #fff !important; margin: 0; padding: 0; }
        .print-page {
          font-family: 'IBM Plex Sans', system-ui, sans-serif;
          width: 194mm;
          max-width: 194mm;
          min-height: 281mm;
          margin: 0 auto;
          border: 1.5px solid #242321;
          padding: 8mm !important;
          background: #fff;
          box-sizing: border-box;
        }
        /* Mobile screen preview — scale the fixed A4 canvas down so the whole page fits */
        @media screen and (max-width: 820px) {
          html, body { overflow-x: auto; }
          .print-page {
            transform: scale(0.48);
            transform-origin: top left;
            margin: 0 !important;
          }
          .print-page-shell {
            width: 100vw;
            overflow-x: hidden;
            /* Reserve height so the scaled child doesn't leave whitespace */
            min-height: calc(281mm * 0.48 + 32px);
          }
        }
        @media screen and (min-width: 821px) and (max-width: 1100px) {
          .print-page { transform: scale(0.85); transform-origin: top center; }
        }
        .print-page h1,h2,h3 { font-family: 'Manrope', sans-serif; }
        .print-page table { width: 100%; border-collapse: collapse; font-size: 10.5px; page-break-inside: auto; table-layout: fixed; word-wrap: break-word; }
        .print-page thead { display: table-header-group; }
        .print-page tr { page-break-inside: avoid; page-break-after: auto; }
        .print-page th, .print-page td { border: 1px solid #ccc; padding: 4px 6px; text-align: left; overflow-wrap: break-word; }
        .print-page th { background: #F6F1E7; text-transform: uppercase; font-size: 8.5px; letter-spacing: .06em; }
        .print-page .r { text-align: right; }
        .print-page .mono { font-family: 'IBM Plex Mono', monospace; }
        @media print {
          .no-print { display: none !important; }
          html, body { margin: 0 !important; padding: 0 !important; background: #fff !important; }
          .sidebar { display: none !important; }
          [data-testid="mobile-topbar"] { display: none !important; }
          .print-page {
            width: 194mm !important;
            max-width: 194mm !important;
            min-height: 0 !important;
            margin: 0 auto !important;
            padding: 8mm !important;
            border: 1.5px solid #242321 !important;
            box-shadow: none !important;
            box-sizing: border-box !important;
            page-break-after: avoid;
            transform: none !important;
          }
          /* Reduce font sizes slightly to help fit on 1 page */
          .print-page table { font-size: 9.5px !important; }
          .print-page th, .print-page td { padding: 3px 5px !important; }
          .print-page h1, .print-page h2 { font-size: 15pt !important; }
          .print-page h3 { font-size: 12pt !important; }
        }
      `}</style>
      <div className="print-page-shell">{children}</div>
      <div className="no-print" style={{textAlign:"center", marginTop:32}}>
        <button onClick={()=>window.print()} className="btn-primary">Print / Save as PDF</button>
      </div>
    </div>
  );
}

function DocHeader({ settings, docTitle, docNo, dateStr }) {
  return (
    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", borderBottom:"2px solid #242321", paddingBottom:16, marginBottom:24 }}>
      <div>
        {settings.logo_url
          ? <img src={settings.logo_url} alt="logo" style={{height:44}}/>
          : <Logo size={36} />}
        <div style={{fontSize:11, color:"#555", marginTop:8, lineHeight:1.5}}>
          <div style={{fontWeight:700, color:"#111"}}>{settings.company_name || "Marsh Clothing"}</div>
          {settings.address && <div style={{whiteSpace:"pre-line"}}>{settings.address}</div>}
          {settings.phone && <div>Phone: {settings.phone}</div>}
          {settings.email && <div>Email: {settings.email}</div>}
          {settings.gst_number && <div>GSTIN: <span className="mono">{settings.gst_number}</span></div>}
        </div>
      </div>
      <div style={{textAlign:"right"}}>
        <div style={{fontSize:12, letterSpacing:"0.15em", textTransform:"uppercase", color:"#666"}}>{docTitle}</div>
        <div style={{fontFamily:"IBM Plex Mono, monospace", fontSize:22, fontWeight:700, marginTop:4}}>{docNo}</div>
        <div style={{fontSize:11, color:"#555", marginTop:4}}>{dateStr}</div>
      </div>
    </div>
  );
}

function ItemTable({ items, subtotal, gst_percent, gst_amount, total, gst_breakup }) {
  const zoneLabel = (k) => k.replace(/_/g," ");
  const zoneCost = (cfg, zoneKey) => {
    const c = (cfg && typeof cfg === "object") ? cfg : { method: "screen", colors: 1 };
    if (c.method === "screen")     return 30 + (Math.max(1, Number(c.colors) || 1) - 1) * 15;
    if (c.method === "embroidery") return zoneKey.includes("chest") || zoneKey.includes("sleeve") ? 60 : 0;
    if (c.method === "dtf")        return (Number(c.w) || 0) * (Number(c.h) || 0) * 5;
    return 0;
  };
  const printCostFor = (it) => {
    if (Number(it.customization_price) > 0) return Number(it.customization_price);
    return Object.entries(it.print_locations || {}).reduce((a, [k, v]) => a + zoneCost(v, k), 0);
  };
  const amountFor = (it) => Number(it.qty) * (Number(it.unit_price) + printCostFor(it));
  // Aggregate zones across all items — for the diagram at the bottom
  const allSelected = {};
  (items || []).forEach(it => Object.keys(it.print_locations || {}).forEach(k => { allSelected[k] = true; }));

  return (
    <>
      <table>
        <thead><tr><th style={{width:"4%"}}>#</th><th>Item</th><th>Customization</th><th style={{width:"7%"}}>GST%</th><th style={{width:"8%"}}>Qty</th><th style={{width:"10%"}} className="r">Unit ₹</th><th style={{width:"10%"}} className="r">Cust ₹/pc</th><th style={{width:"12%"}} className="r">Amount ₹</th></tr></thead>
        <tbody>
          {items?.map((it, i) => {
            const pc = printCostFor(it);
            return (
              <tr key={it.id}>
                <td className="mono">{i+1}</td>
                <td>
                  {it.product}{it.color && <span style={{color:"#666"}}> · {it.color}</span>}
                  {it.print_locations && Object.keys(it.print_locations).length > 0 && (
                    <div style={{fontSize:9, color:"#666", marginTop:2}}>Print at: {Object.keys(it.print_locations).map(zoneLabel).join(", ")}</div>
                  )}
                </td>
                <td>{it.customization || "—"}</td>
                <td className="mono r">{it.gst_percent != null ? it.gst_percent : (gst_percent || "—")}</td>
                <td className="mono r">{it.qty}</td>
                <td className="mono r">{Number(it.unit_price).toLocaleString("en-IN")}</td>
                <td className="mono r" style={{color: pc>0 ? "#C45A41" : "#B3AC9E"}}>{pc > 0 ? pc.toLocaleString("en-IN") : "—"}</td>
                <td className="mono r">{amountFor(it).toLocaleString("en-IN")}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <table style={{marginTop:12, width:"55%", marginLeft:"auto"}}>
        <tbody>
          <tr><td>Subtotal</td><td className="mono r">{inr(subtotal)}</td></tr>
          {(gst_breakup && gst_breakup.length > 0
            ? gst_breakup.map(b => (
                <tr key={b.slab}><td>GST @ {b.slab}% on {inr(b.subtotal)}</td><td className="mono r">{inr(b.gst)}</td></tr>
              ))
            : (
                <tr><td>GST ({gst_percent}%)</td><td className="mono r">{inr(gst_amount)}</td></tr>
              ))}
          <tr style={{background:"#F6F1E7", fontWeight:700}}><td>Grand Total</td><td className="mono r">{inr(total)}</td></tr>
        </tbody>
      </table>
      {Object.keys(allSelected).length > 0 && (
        <div style={{marginTop:20, padding:12, background:"#FBF8F1", border:"1px solid #EAE5DB", pageBreakInside:"avoid"}}>
          <div style={{fontSize:10, textTransform:"uppercase", letterSpacing:".08em", color:"#666", marginBottom:8}}>Print / embroidery placement</div>
          <div style={{display:"flex", gap:24, alignItems:"center", flexWrap:"wrap"}}>
            <TShirtSVG side="front" selected={allSelected}/>
            <TShirtSVG side="back" selected={allSelected}/>
            <div style={{fontSize:11, color:"#333", lineHeight:1.6, flex:1, minWidth:200}}>
              <div style={{fontWeight:700, marginBottom:4}}>Highlighted areas:</div>
              <div>{Object.keys(allSelected).map(zoneLabel).join(" · ")}</div>
              <div style={{marginTop:6, fontStyle:"italic", color:"#75706A"}}>Customization cost per piece is broken out per line above. Only the marked areas will be printed / embroidered.</div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function PaymentBlock({ s }) {
  return (
    <div style={{marginTop:20, padding:12, background:"#FBF8F1", border:"1px solid #EAE5DB", fontSize:12}}>
      <div style={{fontSize:10, letterSpacing:".08em", textTransform:"uppercase", color:"#666", marginBottom:6}}>Bank details for payment</div>
      <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:16}}>
        <div style={{lineHeight:1.7}}>
          {s.bank_name && <div>Bank: <b>{s.bank_name}</b></div>}
          {s.account_holder && <div>A/C name: <b>{s.account_holder}</b></div>}
          {s.account_no && <div>A/C no: <span className="mono">{s.account_no}</span></div>}
          {s.ifsc && <div>IFSC: <span className="mono">{s.ifsc}</span></div>}
          {s.upi_id && <div>UPI: <span className="mono">{s.upi_id}</span></div>}
        </div>
        {s.payment_qr_url && <img src={s.payment_qr_url} alt="QR" style={{maxWidth:120, marginLeft:"auto"}}/>}
      </div>
    </div>
  );
}

// ---- Print Invoice ----
export function PrintInvoice() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/print/invoice/${id}`).then(r => setD(r.data)); }, [id]);
  if (!d) return <div style={{padding:40}}>Loading…</div>;
  const { invoice: i, settings: s } = d;
  const dueLeft = Number(i.total) - Number(i.amount_paid || 0);
  const status = i.status === "paid" ? "PAID" : i.status === "partial" ? "PARTIALLY PAID" : "UNPAID";
  return (
    <PrintShell>
      <DocHeader settings={s} docTitle="Tax Invoice" docNo={i.invoice_no}
        dateStr={`Date: ${(i.created_at||"").slice(0,10)}`} />
      <div style={{display:"flex", justifyContent:"space-between", marginBottom:16}}>
        <div>
          <div style={{fontSize:10, color:"#666", textTransform:"uppercase", letterSpacing:".08em"}}>Bill to</div>
          <div style={{fontWeight:700, fontSize:14, marginTop:2}}>{i.customer_name}</div>
        </div>
        <div style={{padding:"4px 12px", border:`2px solid ${status==="PAID"?"#4A7258":status==="UNPAID"?"#B83A3A":"#D99036"}`, color: status==="PAID"?"#4A7258":status==="UNPAID"?"#B83A3A":"#D99036", fontWeight:700, letterSpacing:".1em"}}>{status}</div>
      </div>
      <ItemTable {...i} />
      <div style={{marginTop:12, fontSize:12, fontStyle:"italic", color:"#333"}}>Amount in words: <b>{numToWords(Number(i.total))}</b></div>
      {dueLeft > 0.01 && <PaymentBlock s={s} />}
      {s.invoice_terms && <div style={{marginTop:20, fontSize:10, color:"#555", whiteSpace:"pre-line", borderTop:"1px solid #ccc", paddingTop:10}}><b>Terms & Conditions</b><br/>{s.invoice_terms}</div>}
    </PrintShell>
  );
}

// ---- Print Quote ----
export function PrintQuote() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/print/quote/${id}`).then(r => setD(r.data)); }, [id]);
  if (!d) return <div style={{padding:40}}>Loading…</div>;
  const { quote: q, settings: s } = d;
  return (
    <PrintShell>
      <DocHeader settings={s} docTitle="Quotation" docNo={q.quote_no}
        dateStr={`Date: ${(q.created_at||"").slice(0,10)}${q.valid_until?` · Valid until ${q.valid_until.slice(0,10)}`:""}`} />
      <div style={{marginBottom:16}}>
        <div style={{fontSize:10, color:"#666", textTransform:"uppercase", letterSpacing:".08em"}}>Quote for</div>
        <div style={{fontWeight:700, fontSize:14, marginTop:2}}>{q.customer_name}</div>
      </div>
      <ItemTable {...q} />
      <div style={{marginTop:12, fontSize:12, fontStyle:"italic", color:"#333"}}>Amount in words: <b>{numToWords(Number(q.total))}</b></div>
      <PaymentBlock s={s} />
      {s.quote_terms && <div style={{marginTop:20, fontSize:10, color:"#555", whiteSpace:"pre-line", borderTop:"1px solid #ccc", paddingTop:10}}><b>Terms & Conditions</b><br/>{s.quote_terms}</div>}
    </PrintShell>
  );
}

// ---- Print Order Form (uses order + settings) ----
export function PrintOrderForm() {
  const { id } = useParams();
  const [o, setO] = useState(null);
  const [s, setS] = useState({});
  useEffect(() => {
    api.get(`/orders/${id}`).then(r => setO(r.data));
    api.get("/settings").then(r => setS(r.data));
  }, [id]);
  if (!o) return <div style={{padding:40}}>Loading…</div>;
  const advance = Number(o.advance_amount||0);
  const balance = Number(o.balance_amount ?? (o.total - advance));
  return (
    <PrintShell>
      <DocHeader settings={s} docTitle="Order Form" docNo={o.order_no}
        dateStr={`Date: ${(o.created_at||"").slice(0,10)}${o.expected_delivery_at ? ` · Expected delivery: ${o.expected_delivery_at.slice(0,10)}` : ""}`} />
      <div style={{marginBottom:16, display:"flex", justifyContent:"space-between", flexWrap:"wrap", gap:16}}>
        <div>
          <div style={{fontSize:10, color:"#666", textTransform:"uppercase", letterSpacing:".08em"}}>Customer</div>
          <div style={{fontWeight:700, fontSize:14, marginTop:2}}>{o.customer_name}</div>
          {o.customer_phone && <div style={{fontSize:11, color:"#555"}}>{o.customer_phone}</div>}
        </div>
        <div style={{textAlign:"right", fontSize:11}}>
          <div style={{color:"#666"}}>Advance received: <span className="mono" style={{color:"#111",fontWeight:600}}>{inr(advance)}</span></div>
          <div style={{color:"#666"}}>Balance due: <span className="mono" style={{color:balance>0?"#B83A3A":"#4A7258",fontWeight:700}}>{inr(balance)}</span></div>
        </div>
      </div>
      <ItemTable {...o} />
      {o.items?.some(i => i.sizes && Object.keys(i.sizes).length) && (
        <div style={{marginTop:16}}>
          <div style={{fontSize:10, textTransform:"uppercase", letterSpacing:".08em", color:"#666", marginBottom:6}}>Size breakup</div>
          <table>
            <thead><tr><th>Item</th>{["XS","S","M","L","XL","2XL","3XL","4XL"].map(s => <th key={s} className="r">{s}</th>)}<th className="r">Total</th></tr></thead>
            <tbody>
              {o.items.filter(i=>i.sizes).map(it => (
                <tr key={it.id}>
                  <td>{it.product}</td>
                  {["XS","S","M","L","XL","2XL","3XL","4XL"].map(sz => <td key={sz} className="mono r">{it.sizes?.[sz] || "—"}</td>)}
                  <td className="mono r"><b>{Object.values(it.sizes||{}).reduce((a,b)=>a+Number(b||0),0)}</b></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* T-shirt diagram is rendered by ItemTable now — no duplicate here */}

      <PaymentBlock s={s} />
      {s.invoice_terms && <div style={{marginTop:20, fontSize:10, color:"#555", whiteSpace:"pre-line", borderTop:"1px solid #ccc", paddingTop:10}}><b>Terms</b><br/>{s.invoice_terms}</div>}
      <div style={{marginTop:32, display:"flex", justifyContent:"space-between", fontSize:11, color:"#666"}}>
        <div>
          <div style={{borderTop:"1px solid #333", width:180, marginTop:36}}></div>
          <div style={{marginTop:4}}>Customer signature</div>
        </div>
        <div style={{textAlign:"right"}}>
          <div style={{borderTop:"1px solid #333", width:180, marginTop:36, marginLeft:"auto"}}></div>
          <div style={{marginTop:4}}>Authorised signatory</div>
        </div>
      </div>
    </PrintShell>
  );
}

// ---- Print Job Card (customization vendor's copy — NO prices, SCOPED to their job type) ----
export function PrintJobCard() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const jobId = params.get("job");
  const [o, setO] = useState(null);
  const [s, setS] = useState({});
  useEffect(() => {
    api.get(`/orders/${id}`).then(r => setO(r.data));
    api.get("/settings").then(r => setS(r.data));
  }, [id]);
  if (!o) return <div style={{padding:40}}>Loading…</div>;

  const job = jobId ? (o.customizations || []).find(c => c.id === jobId) : null;
  const jobType = (job?.type || "").toLowerCase(); // "screen" | "embroidery" | "dtf"
  const zoneLabel = (k) => k.replace(/_/g, " ");

  // Zone belongs to this job if its method matches. In unscoped mode all zones are shown.
  const zoneMatches = (cfg) => {
    if (!jobType) return true;
    const c = (cfg && typeof cfg === "object") ? cfg : { method: "screen" };
    const m = (c.method || "screen").toLowerCase();
    if (jobType === "dtf") return m === "dtf";
    if (jobType === "screen") return m === "screen";
    if (jobType === "embroidery") return m === "embroidery";
    return m === jobType;
  };

  // Filter items so only the ones with at least one matching zone appear.
  const scopedItems = (o.items || []).map(it => {
    const pl = it.print_locations || {};
    const kept = {};
    Object.entries(pl).forEach(([k, v]) => { if (zoneMatches(v)) kept[k] = v; });
    return { ...it, _scopedZones: kept };
  }).filter(it => !jobType || Object.keys(it._scopedZones).length > 0);

  const scopedSelected = {};
  scopedItems.forEach(it => { Object.keys(it._scopedZones).forEach(k => { scopedSelected[k] = true; }); });

  return (
    <PrintShell>
      <DocHeader settings={s} docTitle={job ? `Job Card — ${job.type.toUpperCase()}` : "Job Card"} docNo={o.order_no}
        dateStr={`Date: ${(o.created_at||"").slice(0,10)}${o.expected_delivery_at ? ` · Deliver by: ${o.expected_delivery_at.slice(0,10)}` : ""}`} />

      {job && (
        <div style={{marginBottom:16, display:"flex", justifyContent:"space-between", flexWrap:"wrap", gap:16, padding:10, background:"#F6F1E7", border:"1px solid #EAE5DB"}}>
          <div>
            <div style={{fontSize:10, color:"#666", textTransform:"uppercase", letterSpacing:".08em"}}>Assigned to</div>
            <div style={{fontWeight:700, fontSize:14, marginTop:2}}>{job.vendor || "—"}</div>
          </div>
          <div style={{textAlign:"right", fontSize:12}}>
            <div style={{color:"#666"}}>Job type: <b style={{color:"#111", textTransform:"uppercase"}}>{job.type}</b></div>
            {o.expected_delivery_at && <div style={{color:"#666", marginTop:4}}>Deliver by: <b style={{color:"#111"}}>{o.expected_delivery_at.slice(0,10)}</b></div>}
          </div>
        </div>
      )}

      {/* Line items relevant to THIS vendor's scope */}
      <table>
        <thead>
          <tr>
            <th style={{width:"5%"}}>#</th>
            <th>Item / Blank</th>
            <th>Color</th>
            <th className="r" style={{width:"12%"}}>Qty</th>
          </tr>
        </thead>
        <tbody>
          {(scopedItems.length ? scopedItems : o.items || []).map((it, i) => (
            <tr key={it.id}>
              <td className="mono">{i+1}</td>
              <td>
                {it.product}
                {Object.keys(it._scopedZones || it.print_locations || {}).length > 0 && (
                  <div style={{fontSize:9, color:"#666", marginTop:2}}>
                    {job ? `Do ${job.type} at: ` : "Print at: "}
                    {Object.keys(it._scopedZones || it.print_locations).map(zoneLabel).join(", ")}
                  </div>
                )}
              </td>
              <td>{it.color || "—"}</td>
              <td className="mono r"><b>{it.qty}</b></td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Size breakup — only for scoped items */}
      {(scopedItems.length ? scopedItems : o.items || []).some(i => i.sizes && Object.keys(i.sizes).length) && (
        <div style={{marginTop:16}}>
          <div style={{fontSize:10, textTransform:"uppercase", letterSpacing:".08em", color:"#666", marginBottom:6}}>Size breakup</div>
          <table>
            <thead><tr><th>Item</th>{["XS","S","M","L","XL","2XL","3XL","4XL"].map(sz => <th key={sz} className="r">{sz}</th>)}<th className="r">Total</th></tr></thead>
            <tbody>
              {(scopedItems.length ? scopedItems : o.items || []).filter(i=>i.sizes).map(it => (
                <tr key={it.id}>
                  <td>{it.product}</td>
                  {["XS","S","M","L","XL","2XL","3XL","4XL"].map(sz => <td key={sz} className="mono r">{it.sizes?.[sz] || "—"}</td>)}
                  <td className="mono r"><b>{Object.values(it.sizes||{}).reduce((a,b)=>a+Number(b||0),0)}</b></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Print zone details — scoped */}
      {scopedItems.some(i => Object.keys(i._scopedZones || {}).length) && (
        <div style={{marginTop:20, padding:12, background:"#FBF8F1", border:"1px solid #EAE5DB"}}>
          <div style={{fontSize:10, textTransform:"uppercase", letterSpacing:".08em", color:"#666", marginBottom:8}}>
            {job ? `${job.type.toUpperCase()} zone specifications` : "Print zone specifications"}
          </div>
          <table>
            <thead><tr><th>Item</th><th>Zone</th><th>Method</th><th>Detail</th></tr></thead>
            <tbody>
              {scopedItems.flatMap(it => Object.entries(it._scopedZones).map(([zone, cfg]) => {
                const c = (cfg && typeof cfg === "object") ? cfg : { method: "screen", colors: 1 };
                let detail = c.custom_size || "";
                if (!detail) {
                  if (c.method === "screen") detail = `${c.colors || 1} color${(c.colors||1)>1?"s":""}`;
                  else if (c.method === "embroidery") detail = "Max 3″";
                  else if (c.method === "dtf") detail = `${c.w||0}″ × ${c.h||0}″`;
                  else detail = "—";
                }
                return (
                  <tr key={`${it.id}-${zone}`}>
                    <td>{it.product} {it.color && `· ${it.color}`}</td>
                    <td style={{textTransform:"capitalize"}}>{zoneLabel(zone)}</td>
                    <td style={{textTransform:"uppercase"}}>{c.method || "screen"}</td>
                    <td>{detail}</td>
                  </tr>
                );
              }))}
            </tbody>
          </table>
        </div>
      )}

      {/* Reference silhouette — highlights ONLY the areas for this vendor's scope */}
      {Object.keys(scopedSelected).length > 0 && (
        <div style={{marginTop:20, padding:12, background:"#fff", border:"1px solid #EAE5DB", pageBreakInside:"avoid"}}>
          <div style={{fontSize:10, textTransform:"uppercase", letterSpacing:".08em", color:"#666", marginBottom:8}}>
            {job ? `${job.type.toUpperCase()} areas — only these` : "Print areas — only these"}
          </div>
          <div style={{display:"flex", gap:24, alignItems:"center", flexWrap:"wrap"}}>
            <TShirtSVG side="front" selected={scopedSelected} />
            <TShirtSVG side="back" selected={scopedSelected} />
            <div style={{fontSize:11, color:"#333", lineHeight:1.6, flex:1}}>
              <div>Confirm locations with the shopfloor supervisor.</div>
              <div>Return the completed batch with this job card.</div>
            </div>
          </div>
        </div>
      )}

      {job?.print_colors && (
        <div style={{marginTop:14, padding:10, border:"2px solid #C45A41", background:"#FBF4EE", fontSize:12}}>
          <div style={{fontSize:10, textTransform:"uppercase", letterSpacing:".08em", color:"#C45A41", marginBottom:4, fontWeight:700}}>Print / Thread Colors</div>
          <div style={{fontWeight:600, fontSize:14, color:"#111"}}>{job.print_colors}</div>
        </div>
      )}

      {/* Attached artwork / reference file */}
      {job?.attachment_url && (
        <div style={{marginTop:16, padding:10, border:"1px dashed #4A7258", background:"#F5F9F5", fontSize:11}}>
          <div style={{fontSize:10, textTransform:"uppercase", letterSpacing:".08em", color:"#2E4A38", marginBottom:4}}>Attached artwork</div>
          {job.attachment_url.startsWith("data:image") ? (
            <img src={job.attachment_url} alt="artwork" style={{maxWidth:"100%", maxHeight:180, objectFit:"contain"}}/>
          ) : (
            <a href={job.attachment_url} style={{color:"#2E4A38", wordBreak:"break-all"}}>{job.attachment_url}</a>
          )}
          {job.attachment_note && <div style={{marginTop:4, color:"#333"}}>{job.attachment_note}</div>}
        </div>
      )}

      {job?.notes && (
        <div style={{marginTop:12, padding:10, border:"1px dashed #B3AC9E", background:"#fff", fontSize:11}}>
          <b>Special instructions:</b> {job.notes}
        </div>
      )}

      {s.po_terms && (
        <div style={{marginTop:20, fontSize:10, color:"#555", whiteSpace:"pre-line", borderTop:"1px solid #ccc", paddingTop:10}}>
          <b>Terms & Conditions</b><br/>{s.po_terms}
        </div>
      )}

      <div style={{marginTop:24, display:"flex", justifyContent:"space-between", fontSize:11, color:"#666"}}>
        <div>
          <div style={{borderTop:"1px solid #333", width:160, marginTop:36}}></div>
          <div style={{marginTop:4}}>Vendor / operator signature</div>
        </div>
        <div style={{textAlign:"right"}}>
          <div style={{borderTop:"1px solid #333", width:160, marginTop:36, marginLeft:"auto"}}></div>
          <div style={{marginTop:4}}>Received-back stamp</div>
        </div>
      </div>
    </PrintShell>
  );
}

// ---- Print Purchase Order — vendor's copy (rates handled separately) ----
export function PrintPO() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const poId = params.get("po");
  const [o, setO] = useState(null);
  const [s, setS] = useState({});
  useEffect(() => {
    api.get(`/orders/${id}`).then(r => setO(r.data));
    api.get("/settings").then(r => setS(r.data));
  }, [id]);
  if (!o) return <div style={{padding:40}}>Loading…</div>;

  const po = poId ? (o.procurements || []).find(p => p.id === poId) : null;
  const poNoParam = params.get("po_no");
  const pos = po ? [po]
    : (poNoParam ? (o.procurements || []).filter(p => (p.po_no || "").split("/")[0] === poNoParam) : (o.procurements || []));
  const total = pos.reduce((a,b) => a + Number(b.amount || 0), 0); // reserved for future use
  void total;
  const totalQty = pos.reduce((a,b) => a + Number(b.qty || 0), 0);
  const mergedSizes = pos.reduce((acc, p) => {
    Object.entries(p.sizes || {}).forEach(([sz, n]) => { acc[sz] = (acc[sz] || 0) + Number(n || 0); });
    return acc;
  }, {});
  const anySize = Object.keys(mergedSizes).length > 0;

  return (
    <PrintShell>
      <DocHeader settings={s} docTitle="Purchase Order" docNo={po?.po_no || o.order_no}
        dateStr={`Date: ${(o.created_at||"").slice(0,10)}${o.expected_delivery_at ? ` · Deliver by: ${o.expected_delivery_at.slice(0,10)}` : ""}`} />

      {po && (
        <div style={{marginBottom:16, display:"flex", justifyContent:"space-between", flexWrap:"wrap", gap:16, padding:10, background:"#F6F1E7", border:"1px solid #EAE5DB"}}>
          <div>
            <div style={{fontSize:10, color:"#666", textTransform:"uppercase", letterSpacing:".08em"}}>Vendor</div>
            <div style={{fontWeight:700, fontSize:14, marginTop:2}}>{po.vendor_name}</div>
          </div>
          <div style={{textAlign:"right", fontSize:12}}>
            <div style={{color:"#666"}}>Ref order: <b style={{color:"#111"}}>{o.order_no}</b></div>
            {po.expected_at && <div style={{color:"#666", marginTop:4}}>Expected by: <b style={{color:"#111"}}>{po.expected_at}</b></div>}
          </div>
        </div>
      )}

      <table>
        <thead>
          <tr>
            <th style={{width:"4%"}}>#</th>
            <th>Material</th>
            <th>PO #</th>
            <th className="r" style={{width:"9%"}}>Qty</th>
            {anySize && <th style={{width:"32%"}}>Sizes</th>}
            <th style={{width:"14%"}}>Expected by</th>
          </tr>
        </thead>
        <tbody>
          {pos.map((p, i) => (
            <tr key={p.id}>
              <td className="mono">{i+1}</td>
              <td>{p.material}</td>
              <td className="mono">{p.po_no || "—"}</td>
              <td className="mono r">{p.qty}</td>
              {anySize && (
                <td className="mono" style={{fontSize:9}}>
                  {Object.keys(p.sizes || {}).length > 0
                    ? Object.entries(p.sizes).map(([sz,n]) => `${sz}·${n}`).join("  ")
                    : "—"}
                </td>
              )}
              <td className="mono">{p.expected_at || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {pos.length > 1 && (
        <table style={{marginTop:8, width:"55%", marginLeft:"auto"}}>
          <tbody>
            <tr style={{background:"#F6F1E7", fontWeight:700}}>
              <td>Total qty across {pos.length} lines</td>
              <td className="mono r">{totalQty} pcs</td>
            </tr>
          </tbody>
        </table>
      )}

      {anySize && (
        <div style={{marginTop:10, padding:8, background:"#FBF8F1", border:"1px solid #EAE5DB", fontSize:10}}>
          <div style={{fontSize:9, textTransform:"uppercase", letterSpacing:".08em", color:"#666", marginBottom:4}}>Combined size breakup</div>
          <div style={{fontFamily:"IBM Plex Mono, monospace"}}>
            {Object.entries(mergedSizes).map(([sz, n]) => (
              <span key={sz} style={{marginRight:12}}><b>{sz}</b>·{n}</span>
            ))}
          </div>
        </div>
      )}

      {s.po_terms && (
        <div style={{marginTop:20, fontSize:10, color:"#555", whiteSpace:"pre-line", borderTop:"1px solid #ccc", paddingTop:10}}>
          <b>Terms & Conditions</b><br/>{s.po_terms}
        </div>
      )}

      <div style={{marginTop:32, display:"flex", justifyContent:"space-between", fontSize:11, color:"#666"}}>
        <div>
          <div style={{borderTop:"1px solid #333", width:180, marginTop:36}}></div>
          <div style={{marginTop:4}}>Vendor signature</div>
        </div>
        <div style={{textAlign:"right"}}>
          <div style={{borderTop:"1px solid #333", width:180, marginTop:36, marginLeft:"auto"}}></div>
          <div style={{marginTop:4}}>Authorised signatory (Marsh Clothing)</div>
        </div>
      </div>
    </PrintShell>
  );
}


/** Zone geometry mirroring TShirtDiagram.jsx layout. */
const PRINT_ZONE_GEOM = {
  left_chest:   { side: "front", x: 108, y: 70,  w: 30, h: 22, label: "L-Chest" },
  right_chest:  { side: "front", x: 62,  y: 70,  w: 30, h: 22, label: "R-Chest" },
  center_chest: { side: "front", x: 75,  y: 100, w: 50, h: 38, label: "Centre" },
  left_sleeve:  { side: "front", x: 155, y: 60,  w: 22, h: 20, label: "L-Slv" },
  right_sleeve: { side: "front", x: 23,  y: 60,  w: 22, h: 20, label: "R-Slv" },
  neck_label:   { side: "back",  x: 82,  y: 30,  w: 36, h: 12, label: "Neck" },
  back_upper:   { side: "back",  x: 62,  y: 55,  w: 76, h: 32, label: "Upper" },
  back_full:    { side: "back",  x: 55,  y: 95,  w: 90, h: 100, label: "Full Back" },
};

/**
 * Print-friendly T-shirt. If `selected` is passed (array/object of zone keys)
 * ONLY those zones are drawn — everything else stays hidden.
 * When `selected` is nullish (default), all reference boxes are drawn (legacy behaviour).
 */
function TShirtSVG({ side, selected }) {
  const filterMode = !!selected;
  const activeSet = filterMode
    ? new Set(Array.isArray(selected) ? selected : Object.keys(selected || {}))
    : null;
  const zones = Object.entries(PRINT_ZONE_GEOM).filter(([_, z]) => z.side === side);

  return (
    <svg width="120" height="140" viewBox="0 0 200 240" xmlns="http://www.w3.org/2000/svg">
      <path d="M40 30 L80 20 Q100 40 120 20 L160 30 L180 80 L150 90 L150 220 L50 220 L50 90 L20 80 Z"
        fill="#F6F1E7" stroke="#242321" strokeWidth="2"/>
      {zones.map(([key, z]) => {
        const active = filterMode ? activeSet.has(key) : false;
        // In "reference" mode (no selection provided), show dashed hints for chest/back regions only.
        if (filterMode && !active) return null;
        const drawInReferenceMode = !filterMode && ["center_chest","back_full","right_sleeve"].includes(key);
        if (!filterMode && !drawInReferenceMode) return null;
        return (
          <g key={key}>
            <rect x={z.x} y={z.y} width={z.w} height={z.h}
              fill={active ? "rgba(196,90,65,0.35)" : "none"}
              stroke={active ? "#C45A41" : (key.includes("sleeve") ? "#3D5A80" : "#C45A41")}
              strokeWidth={active ? 2 : 1.5}
              strokeDasharray={active ? "0" : "4 3"} />
            {active && (
              <text x={z.x + z.w/2} y={z.y + z.h/2 + 3}
                fontSize="8" fill="#7C2626" textAnchor="middle" fontFamily="Manrope" fontWeight="700">
                {z.label}
              </text>
            )}
          </g>
        );
      })}
      <text x="100" y="235" fontSize="10" fill="#242321" textAnchor="middle" fontFamily="Manrope" fontWeight="700">{side.toUpperCase()}</text>
    </svg>
  );
}
